import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/lib/apiClient";
import type { Pc } from "@/types/pc";
import type { User, CurrentUser } from "@/types/user";
import type { Reservation } from "@/types/reservation";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ReservationModal } from "@/components/ReservationModal";
import { MonthCalendar } from "@/calendar/MonthCalendar";
import { MobileAgenda } from "@/calendar/MobileAgenda";
import { PcScheduleGrid } from "@/calendar/PcScheduleGrid";
import { reservationsToEvents } from "@/calendar/calendarUtils";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { dateKey } from "@/lib/date";

type ViewMode = "month" | "week" | "pc" | "list";

function buildDayKeys(startOffset: number, count: number): string[] {
  const today = new Date();
  const keys: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(today.getTime() + (startOffset + i) * 86400000);
    keys.push(dateKey(d.toISOString()));
  }
  return keys;
}

export function CalendarPage({ currentUser }: { currentUser: CurrentUser }) {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [pcs, setPcs] = useState<Pc[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Reservation | null>(null);
  const [view, setView] = useState<ViewMode>("month");
  const [pcDayOffset, setPcDayOffset] = useState(0);
  const isMobile = useIsMobile();

  const load = () => {
    setLoading(true);
    setError(null);
    const from = new Date(Date.now() - 30 * 86400000).toISOString();
    const to = new Date(Date.now() + 90 * 86400000).toISOString();
    Promise.all([
      api.get<Reservation[]>(`/reservations?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
      api.get<Pc[]>("/pcs"),
      currentUser.role !== "user" ? api.get<User[]>("/users") : Promise.resolve<User[]>([]),
    ])
      .then(([r, p, u]) => {
        setReservations(r);
        setPcs(p);
        setUsers(u);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [currentUser.role]);

  const pcById = useMemo(() => new Map(pcs.map((p) => [p.id, p])), [pcs]);
  const userNameById = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, u.displayName]));
    if (!map.has(currentUser.id)) map.set(currentUser.id, currentUser.displayName);
    return map;
  }, [users, currentUser]);

  const pcName = (id: number) => pcById.get(id)?.name ?? `PC#${id}`;
  const userName = (id: number) => userNameById.get(id) ?? `利用者#${id}`;

  if (loading) return <LoadingState />;
  if (error) return <EmptyState message={error} />;

  const events = reservationsToEvents(reservations, pcById, userNameById);
  const pcDays = buildDayKeys(pcDayOffset, 14);

  return (
    <div>
      <h1 className="page-title">利用カレンダー</h1>
      <p className="page-subtitle">PCの予約状況を確認できます。</p>

      {!isMobile && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {(
            [
              ["month", "月表示"],
              ["week", "週表示"],
              ["pc", "PC別表示"],
              ["list", "一覧表示"],
            ] as [ViewMode, string][]
          ).map(([mode, label]) => (
            <button
              key={mode}
              className={view === mode ? "btn btn-primary" : "btn btn-secondary"}
              style={{ height: 36, padding: "0 14px" }}
              onClick={() => setView(mode)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="card card-padded">
        {isMobile ? (
          <MobileAgenda
            reservations={reservations}
            pcNameFor={pcName}
            userNameFor={userName}
            onSelectReservation={setSelected}
          />
        ) : view === "pc" ? (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <button className="btn btn-secondary" style={{ height: 36 }} onClick={() => setPcDayOffset((v) => v - 7)}>
                ＜ 前の週
              </button>
              <button className="btn btn-secondary" style={{ height: 36 }} onClick={() => setPcDayOffset((v) => v + 7)}>
                次の週 ＞
              </button>
            </div>
            <PcScheduleGrid
              pcs={pcs}
              reservations={reservations}
              days={pcDays}
              userNameFor={userName}
              onSelectReservation={setSelected}
            />
          </div>
        ) : (
          <MonthCalendar
            events={events}
            initialView={view === "week" ? "timeGridWeek" : view === "list" ? "listWeek" : "dayGridMonth"}
            onEventClick={(id) => {
              const r = reservations.find((x) => x.id === id);
              if (r) setSelected(r);
            }}
          />
        )}
      </div>

      {selected && (
        <ReservationModal
          reservation={selected}
          pcName={pcName(selected.pc_id)}
          userName={userName(selected.user_id)}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
